import { DOCS_URLS } from "@p0security/frontend-core";
import {
  type FrontendInstallContext,
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { OnePasswordComponents } from "@p0security/integrations-one-password-shared/components";
import {
  VAULT_OUTSIDE_CONNECTOR_CLOUD,
  renderHosting,
} from "@p0security/integrations-one-password-shared/hosting";
import type {
  OnePasswordAccountConfig,
  OnePasswordConnectorConfig,
  OnePasswordIntegration,
} from "@p0security/integrations-one-password-shared/types";
import { p0InvokerEmail } from "@p0security/integrations-vaulted-credential-shared";
import {
  installedItem,
  installedItems,
} from "@p0security/shared/install/installed";
import {
  Alert,
  Collapse,
  CommandDisplay,
  Flex,
  Spacing,
  Typography,
} from "@p0security/ui";
import { assertNever } from "@p0security/util/assertNever";
import { compact, sortBy } from "lodash-es";
import type { ReactNode } from "react";

import {
  type ReadGrantBlock,
  isSecretAccountBlock,
  renderReadGrantTerraform,
} from "./account-iac.ts";
import { renderConnectorTerraform } from "./connector-iac.ts";
import { AccountSteps, ConnectorHelp, type ReadGrant } from "./steps.tsx";

const { Link, Paragraph } = Typography;

type OnePasswordInstaller = FrontendInstaller<typeof OnePasswordComponents>;
type InstallContext = FrontendInstallContext<OnePasswordIntegration>;

/** Creates the provider that lists P0's installs of `integration`'s
 * iam-write component: the accounts, or projects, that a field placing a
 * connector or a secret can name. */
const newIamWriteInstallsProvider = (integration: string) =>
  otherInstalledIntegration({ component: "iam-write", integration });

/** The connectors an account can name: this integration's own installed
 * connectors, each with where it runs.
 *
 * Read from the installer's live config rather than fetched: the pointer
 * targets a sibling component, not another integration, so
 * `otherInstalledIntegration` would spend a read on a document the install
 * context already holds.
 */
const listConnectors = async ({ config }: InstallContext) =>
  sortBy(
    compact(
      Object.entries(config ? installedItems("connector", config) : {}).map(
        ([id, connector]) =>
          connector && {
            id,
            label: `${id}: ${renderHosting(connector.hosting)}`,
          }
      )
    ),
    "label"
  );

/** Whether a stored item has been set up: its page then shows the form, and
 * its Terraform only on request. */
const isSetUp = (item: { state?: string } | undefined) =>
  item?.state === "configure" || item?.state === "installed";

const DocsLink: React.FC = () => (
  <Link
    href={DOCS_URLS.ONEPASSWORD_INSTALL}
    rel="noopener noreferrer"
    target="_blank"
  >
    1Password install guide
  </Link>
);

/** Why a connector on Cloud Run can't be set up yet: its Terraform lets in
 * the service account P0 invokes it as, which is the one P0's Google Cloud
 * integration has. */
const MISSING_GOOGLE_CLOUD =
  "P0 invokes a connector on Cloud Run as the service account of its Google Cloud integration, which isn't installed. Install P0's Google Cloud IAM management for the connector's project first.";

/** Says why a connector on Cloud Run has no Terraform to show. */
const MissingGoogleCloud: React.FC = () => (
  <Alert message={MISSING_GOOGLE_CLOUD} showIcon type="error" />
);

/** Shown above the new connector's form. */
const NewConnectorPreamble: React.FC = () => (
  <>
    <Paragraph>
      P0 shares items from your 1Password vaults through a connector that you
      deploy in your own AWS account or Google Cloud project. Only the connector
      reads an account&apos;s service account token, and it shares items only
      with the email domains you allow on its deployment.
    </Paragraph>
    <Paragraph>
      One connector can serve many 1Password accounts, so one is usually enough.
      Have ready P0&apos;s AWS IAM management or Google Cloud IAM management
      integration for the account or project the connector will run in. Where it
      runs can&apos;t change once the connector is added. See the <DocsLink />.
    </Paragraph>
  </>
);

/** Shown above the new account's form. */
const NewAccountPreamble: React.FC = () => (
  <>
    <Paragraph>
      Add each 1Password account that P0 shares items from, by the address you
      sign in to it at. An account names the connector that shares its items,
      and the secret, in your own secret manager, that holds the token of the
      account&apos;s service account. People can request items from the vaults
      that service account can see, and no others. Service accounts need a
      1Password Business or Enterprise plan, and can&apos;t access Personal,
      Private or Employee vaults.
    </Paragraph>
    <Paragraph>
      Install a connector first. {VAULT_OUTSIDE_CONNECTOR_CLOUD} It can be in
      another account or project. Have ready P0&apos;s AWS Secrets Manager or
      Google Secret Manager integration for where the secret is. None of this
      can change once the account is added. The next page shows how to create
      the service account, store its token and let the connector read it. See
      the <DocsLink />.
    </Paragraph>
  </>
);

/** What to do with one configuration of the account's read grant, shown
 * above it. `last` is whether "Next" follows it. */
const toGrantHeader = (block: ReadGrantBlock, { last }: { last: boolean }) => {
  const next = last ? <> Then click &quot;Next&quot;.</> : null;
  switch (block.purpose) {
    case "grant":
      return (
        <Paragraph>
          Add the following to your Terraform and apply it. It lets the
          connector read this account&apos;s service account token.{next}
        </Paragraph>
      );
    case "secret-account":
      return (
        <>
          <Paragraph>
            Then add the following where you manage AWS account{" "}
            {block.accountId}, which holds the secret, and apply it. Add its
            output to the policy of the secret&apos;s KMS key.{next}
          </Paragraph>
          <Spacing mb={12}>
            <Alert
              message="This replaces the secret's resource policy. If the secret already has one, add the statement to that policy instead."
              showIcon
              type="warning"
            />
          </Spacing>
        </>
      );
    default:
      throw assertNever(block);
  }
};

/** What a set-up account's page calls each configuration of its read
 * grant. */
const GRANT_REFERENCE_LABELS: Record<ReadGrantBlock["purpose"], string> = {
  grant: "Show the Terraform that lets the connector read the token",
  "secret-account": "Show the Terraform for the secret's account",
};

/** A set-up item's Terraform, collapsed, for an admin who needs it again. */
const TerraformReference: React.FC<{
  blocks: { key: string; label: string; terraform: string }[];
  children?: ReactNode;
}> = ({ blocks, children }) => (
  <Flex gap={8} vertical>
    {children}
    <Collapse
      items={blocks.map(({ key, label, terraform }) => ({
        children: <CommandDisplay commands={terraform} maxRows={20} />,
        key,
        label,
      }))}
    />
  </Flex>
);

/** Renders an account's read-grant Terraform from what the item and its
 * connector store, or says why it can't be. */
const toReadGrant = (
  context: InstallContext,
  accountId: string,
  item: OnePasswordAccountConfig
): ReadGrant => {
  const hosting = context.config
    ? installedItem("connector", context.config, item.connector)?.hosting
    : undefined;
  if (!hosting) return { status: "no-connector" };
  const blocks = renderReadGrantTerraform({
    accountId,
    connectorId: item.connector,
    hosting,
    item,
  });
  if (!blocks) return { hosting, status: "secret-outside-cloud" };
  return {
    blocks,
    hosting,
    secretAccountId: blocks.find(isSecretAccountBlock)?.accountId,
    status: "ready",
  };
};

/** The configurations of a read grant, which has none until it's ready. */
const toBlocks = (grant: ReadGrant): ReadGrantBlock[] => {
  switch (grant.status) {
    case "no-connector":
    case "secret-outside-cloud":
      return [];
    case "ready":
      return grant.blocks;
    default:
      throw assertNever(grant);
  }
};

const renderConnectorInstructions = (
  context: InstallContext,
  connectorId: string,
  { hosting }: OnePasswordConnectorConfig
) => renderConnectorTerraform({ connectorId, context, hosting });

/**
 * Installs the 1Password integration.
 *
 * A connector comes first: the new item's form takes its ID and where it
 * runs, and its page shows the Terraform that deploys it above the "Next"
 * that checks P0 can invoke it, and "Finish" on the next page installs it.
 * Each 1Password account, keyed by its sign-in address, then names a connector
 * and the secret that holds its service account token, and its page shows the
 * steps to create the service account, store its token and let the connector
 * read it, above the "Next" that validates the install through the connector.
 * "Finish" on the next page installs it.
 *
 * Once an item is set up, its page keeps its Terraform, collapsed.
 */
export const onePasswordInstaller: OnePasswordInstaller = {
  components: {
    connector: {
      instructions: (context, id, item) => {
        const terraform = renderConnectorInstructions(context, id, item);
        return terraform === undefined
          ? { help: <MissingGoogleCloud /> }
          : {
              commands: { iac: [{ command: terraform }] },
              help: (
                <ConnectorHelp
                  adminEmail={context.user?.email}
                  connectorId={id}
                  hosting={item.hosting}
                />
              ),
            };
      },
      items: {
        hosting: {
          labeler: () =>
            "Where the connector runs. Its Terraform deploys it there, and P0 invokes it there.",
          options: {
            aws: {
              items: {
                accountId: {
                  labeler: () =>
                    "The AWS account the connector runs in. P0 lists the accounts where its AWS IAM management is installed.",
                  optionProvider: newIamWriteInstallsProvider("aws"),
                },
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Lambda function. Its Terraform creates the function, and an IAM role of the same name that the connector runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The AWS region the connector runs in, such as us-west-2.",
                },
              },
            },
            gcp: {
              items: {
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Cloud Run service, in 6 to 30 characters. Its Terraform creates the service, and a service account of the same name that the connector runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The Google Cloud region the connector runs in, such as us-west1.",
                },
                projectId: {
                  labeler: () =>
                    "The Google Cloud project the connector runs in. P0 lists the projects where its Google Cloud IAM management is installed.",
                  optionProvider: newIamWriteInstallsProvider("gcloud"),
                },
              },
              prerequisiteMessages: async (context) =>
                p0InvokerEmail(context.installed)
                  ? undefined
                  : MISSING_GOOGLE_CLOUD,
            },
          },
        },
      },
      labeler: () =>
        "A name for this connector in P0, such as prod. Each 1Password account you add picks its connector by this name.",
      newPreamble: () => <NewConnectorPreamble />,
      // The framework passes the configure form's draft, so this reads the
      // item P0 stored.
      preamble: (context, id) => {
        const stored = context.config?.connector?.[id];
        if (!stored || !isSetUp(stored)) return undefined;
        const terraform = renderConnectorInstructions(context, id, stored);
        return terraform === undefined ? (
          <MissingGoogleCloud />
        ) : (
          <TerraformReference
            blocks={[
              {
                key: "terraform",
                label: "Show the connector's Terraform",
                terraform,
              },
            ]}
          />
        );
      },
    },
    account: {
      // The item was saved with every field the Terraform needs, so it
      // renders from what the item and its connector store.
      instructions: (context, accountId, item) => {
        const grant = toReadGrant(context, accountId, item);
        const blocks = toBlocks(grant);
        return {
          commands:
            blocks.length > 0
              ? {
                  iac: blocks.map((block, index) => ({
                    command: block.terraform,
                    header: toGrantHeader(block, {
                      last: index === blocks.length - 1,
                    }),
                  })),
                }
              : undefined,
          help: (
            <AccountSteps
              accountId={accountId}
              adminEmail={context.user?.email}
              grant={grant}
              item={item}
            />
          ),
        };
      },
      items: {
        connector: {
          optionProvider: listConnectors,
          prerequisiteMessages: async (context) =>
            (await listConnectors(context)).length === 0
              ? "Install a 1Password connector first."
              : undefined,
        },
        secretManager: {
          labeler: () =>
            "Where the service account token is stored. It must be in the connector's cloud, and can be in another account or project.",
          options: {
            "aws-sm": {
              items: {
                install: {
                  labeler: () =>
                    "The AWS account the secret is in. P0 lists the accounts where its AWS Secrets Manager integration is installed.",
                  optionProvider: newIamWriteInstallsProvider("aws-sm"),
                },
                secretsRegion: {
                  labeler: () =>
                    "The AWS region the secret is in, such as us-west-2.",
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  labeler: () =>
                    "The Google Cloud project the secret is in. P0 lists the projects where its Google Secret Manager integration is installed.",
                  optionProvider: newIamWriteInstallsProvider("gcp-sm"),
                },
              },
            },
          },
        },
      },
      labeler: () =>
        "The address you sign in to 1Password at, such as acme.1password.com, without https://. It ends in .1password.com, .1password.ca or .1password.eu.",
      newPreamble: () => <NewAccountPreamble />,
      // As the connector's: the stored item, which also has the allowed
      // domains the last validation read.
      preamble: (context, accountId) => {
        const stored = context.config?.account?.[accountId];
        if (!stored || !isSetUp(stored)) return undefined;
        const grant = toReadGrant(context, accountId, stored);
        return (
          <TerraformReference
            blocks={toBlocks(grant).map(({ purpose, terraform }) => ({
              key: purpose,
              label: GRANT_REFERENCE_LABELS[purpose],
              terraform,
            }))}
          >
            <AccountSteps
              accountId={accountId}
              adminEmail={context.user?.email}
              grant={grant}
              item={stored}
            />
          </TerraformReference>
        );
      },
    },
  },
};
